/*
  Carrinho da Hogo: um dropdown pendurado na navbar, irmao do painel do menu.

  Continua se chamando cart-drawer porque o resto do tema procura por esse nome:
  product-form.js pega document.querySelector('cart-drawer') para abrir o
  carrinho depois do "add to cart", e cart.js usa os IDs CartDrawer-*. O que
  muda aqui e so o jeito de abrir — nada de gaveta em cima da tela.

  A conversa com o menu acontece por eventos no document (hogo:cart:open /
  hogo:menu:open) para os dois arquivos nao dependerem da ordem de carregamento.
*/

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.header = document.querySelector('.custom-header');
    this.setHeaderCartIconAccessibility();

    document.addEventListener('hogo:menu:open', () => this.close());

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.isOpen()) return;
      this.close();
      if (this.cartLink) this.cartLink.focus();
    });

    // O painel vive dentro do header, entao qualquer clique fora dele fecha.
    document.addEventListener('click', (event) => {
      if (!this.isOpen()) return;
      if (event.target.closest('.custom-header__panel')) return;
      if (event.target.closest('.cart-panel')) return;
      if (event.target.closest('#cart-icon-bubble')) return;
      this.close();
    });

    document.querySelectorAll('.js-menu-close').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    // A barra de rolagem da lista acompanha a rolagem e qualquer redesenho
    // do painel (item novo, quantidade, remocao), venha de onde vier.
    this.addEventListener('scroll', () => this.updateScrollbar(), true);
    new MutationObserver(() => requestAnimationFrame(() => this.updateScrollbar())).observe(this, {
      childList: true,
      subtree: true,
    });
    window.addEventListener('resize', () => this.updateScrollbar());
  }

  /*
    Mostra a barra so quando a lista passa da altura do painel, do tamanho da
    lista, com o marcador proporcional ao quanto dela cabe na tela.
  */
  updateScrollbar() {
    const items = this.querySelector('.cart-panel__items');
    const bar = this.querySelector('.cart-panel__scrollbar');
    if (!items || !bar) return;

    const visible = items.clientHeight;
    const total = items.scrollHeight;
    const overflow = total - visible > 1;
    bar.hidden = !overflow;
    if (!overflow) return;

    const thumb = bar.firstElementChild;
    const thumbHeight = Math.max(24, (visible * visible) / total);
    const travel = visible - thumbHeight;
    const progress = items.scrollTop / (total - visible);

    bar.style.top = `${items.offsetTop}px`;
    bar.style.height = `${visible}px`;
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${travel * progress}px)`;
  }

  get cartLink() {
    return document.querySelector('#cart-icon-bubble');
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = this.cartLink;
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.setAttribute('aria-controls', 'CartDrawer');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.isOpen() ? this.close() : this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() !== 'SPACE') return;
      event.preventDefault();
      this.isOpen() ? this.close() : this.open(cartLink);
    });
  }

  open(triggeredBy) {
    const wasOpen = this.isOpen();
    if (triggeredBy) this.setActiveElement(triggeredBy);

    document.dispatchEvent(new CustomEvent('hogo:cart:open'));

    this.classList.add('is-open');
    if (this.header) this.header.classList.add('cart-is-open');
    if (this.cartLink) this.cartLink.setAttribute('aria-expanded', 'true');

    // Sempre abre do topo, mesmo que tenha sido fechado rolado ate o fim. A
    // lista rola quando ha itens; vazio, quem rola e o corpo do painel. So ao
    // abrir de fato: depois de um "add to cart" com o painel ja aberto, ele
    // tambem passa por aqui e nao deve pular de lugar.
    if (!wasOpen) {
      this.querySelectorAll('.cart-panel__items, .cart-panel__body').forEach((el) => {
        el.scrollTop = 0;
      });
    }

    this.lockPageScroll();
    requestAnimationFrame(() => this.updateScrollbar());
  }

  close() {
    if (!this.isOpen()) return;

    this.classList.remove('is-open');
    if (this.header) this.header.classList.remove('cart-is-open');
    if (this.cartLink) this.cartLink.setAttribute('aria-expanded', 'false');

    // O cart.js prende o foco no painel depois de mudar a quantidade; solta.
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);

    this.unlockPageScroll();
  }

  // Com o carrinho aberto a pagina por tras fica parada, em qualquer tela: so a
  // lista de itens rola. O jeito de segurar mora em smooth-scroll.js
  // (window.hogoPageLock), o mesmo do menu.
  lockPageScroll() {
    if (window.hogoPageLock) window.hogoPageLock.lock();
  }

  unlockPageScroll() {
    if (window.hogoPageLock) window.hogoPageLock.unlock();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // O cart.js procura o input pelo id da pagina /cart (#Quantity-N). No painel
  // ele se chama #Drawer-quantity-N, e sem este ajuste a validacao de
  // quantidade (passar do estoque, por exemplo) estourava no console.
  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${id}`);
    if (!input) return;

    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  // Idem para a atualizacao vinda de fora (outra aba, app): o cart.js troca
  // pedacos com os nomes do Dawn. Aqui basta redesenhar o corpo do painel.
  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.querySelector('.cart-panel__body');
        const target = document.querySelector('.cart-panel__body');
        if (!source || !target) return;

        target.innerHTML = source.innerHTML;

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.classList.toggle('is-empty', !html.querySelector('.cart-panel__item'));
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        // A troca cobre a lista, o subtotal e o botao de checkout de uma vez:
        // remover o ultimo item precisa trazer o estado vazio junto.
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-panel__body',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
